import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { toSignal } from '@angular/core/rxjs-interop';
import { Component, computed, effect, ElementRef, inject, signal, untracked, viewChild } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { FormBuilder, FormsModule, NgForm, ReactiveFormsModule, Validators } from '@angular/forms';

import { MatToolbarModule } from '@angular/material/toolbar';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { MatButtonModule } from '@angular/material/button';
import { map } from 'rxjs';
import { DownloadFormat, DownloadService, HttpMethod } from '../services/download/download-svc.ng';
import { ErrorService } from '../services/error/error.service';
import { SnackBarModule, SnackBarService } from '@polk_county_public_schools/ngx-pcps-dialogs/snackbar';

@Component({
    selector: 'app-root',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatToolbarModule,
        MatFormFieldModule,
        MatInputModule,
        MatButtonModule,
        MatRadioModule,
        FormsModule,
        SnackBarModule,
    ],
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
})
export class AppComponent {
    // #region Injects / View Children

    protected readonly downloadSvc = inject(DownloadService);
    protected readonly errorSvc = inject(ErrorService);
    protected readonly fb = inject(FormBuilder);
    protected readonly grpctrl = viewChild(NgForm);
    protected readonly snackSvc = inject(SnackBarService);
    protected readonly teacherCtrl = viewChild<ElementRef<HTMLInputElement>>('teacherctrl');

    // #endregion

    // #region Properties

    protected readonly certForm = this.fb.group({
        TeacherName: this.fb.control<string>('Mrs. Someone - Music Specialist', {
            nonNullable: true,
            validators: Validators.required,
        }),
        SchoolName: this.fb.control<string>('Some Elementary School', {
            nonNullable: true,
            validators: [Validators.required, Validators.maxLength(32)],
        }),
        GraphicsChoice: this.fb.control<string>('music', { nonNullable: true, validators: Validators.required }),
        Cause1: this.fb.control<string>('has been awarded this certificate for excellent', {
            nonNullable: true,
            validators: [Validators.required, Validators.maxLength(60)],
        }),
        Cause2: this.fb.control<string>('achievement and participation in school.', {
            nonNullable: true,
            validators: [Validators.maxLength(60)],
        }),
        Students: this.fb.control<string>('', {
            nonNullable: true,
            validators: [Validators.required],
        }),
    });

    protected readonly sGraphic = toSignal(
        this.certForm.controls.GraphicsChoice.valueChanges.pipe(
            map((val) => {
                return val === 'music' ? 'notes.png' : val === 'art' ? 'palette.png' : '';
            })
        ),
        { initialValue: 'notes.png' }
    );

    // #endregion
    constructor(private http: HttpClient) {
        const loadEffect = effect(() => {
            const tctrl = this.teacherCtrl();

            if (tctrl) {
                untracked(() => {
                    tctrl.nativeElement.focus();

                    loadEffect.destroy();
                });
            }
        });
    }

    title = 'CertMaker';

    // #region Clear

    clear() {
        this.certForm.reset();
        this.grpctrl()?.resetForm();
        this.teacherCtrl()?.nativeElement?.focus();
    }

    // #endregion

    // #region Download Certificate

    downloadCertificate() {
        const certVal = this.certForm.value;

        if (!this.certForm.valid) {
            if (!certVal.TeacherName) this.snackSvc.alert('No teacher name provided.');
            else if (!certVal.SchoolName) this.snackSvc.alert('No school name provided.');
            else if (!certVal.GraphicsChoice) this.snackSvc.alert('No graphics choice made.');
            else if (!certVal.Cause1) this.snackSvc.alert('No recognition line #1 provided.');
            else if (!certVal.Students) this.snackSvc.alert('No students provided.');

            return;
        }

        this.downloadSvc
            .RetrieveDownload(
                HttpMethod.POST,
                'api/PDF',
                {
                    TeacherName: certVal.TeacherName,
                    SchoolName: certVal.SchoolName,
                    Graphic: certVal.GraphicsChoice,
                    RecognitionLine1: certVal.Cause1,
                    RecognitionLine2: certVal.Cause2,
                    Students: certVal.Students,
                },
                DownloadFormat.PDF
            )
            .subscribe({
                next: (info) => {},
                error: (err) => this.errorSvc.handleServiceError(err),
            });
    }

    // #endregion
}
